import React, { useEffect } from "react";
import { Box, IconButton, Tooltip, Typography, useTheme } from "@mui/material";
import QrCodeIcon from "@mui/icons-material/QrCode";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
// useBillingCompanyContext removed as it's no longer needed
import { useAuth } from "../../contexts/AuthProvider/AuthProvider";
import { isBaileyProvider, isMetaProvider } from "../../contexts/AuthProvider/AuthProvider.helpers";

// Interface removed as it's no longer needed

interface IWhatsAppMessageTypeToggleProps {
  useQRSend: boolean;
  onToggleChange: (useQRSend: boolean) => void;
  onDocumentRestrictionChange?: (isRestricted: boolean) => void;
  size?: "small" | "medium" | "large";
  sx?: any;
  leftText?: string;
  rightText?: string;
  isQRAvailable?: boolean;
  shouldShowQRToggle?: boolean;
}

export const WhatsAppMessageTypeToggle: React.FC<
  IWhatsAppMessageTypeToggleProps
> = ({
  useQRSend,
  onToggleChange,
  onDocumentRestrictionChange,
  size = "small",
  sx = {},
  leftText,
  rightText,
  shouldShowQRToggle = false,
}) => {
  const theme = useTheme();

  const { whatsappService } = useAuth();
  const isMetaAvailable = isMetaProvider(whatsappService);
  const isQRAvailable = isBaileyProvider(whatsappService);

  useEffect(() => {
    if (onDocumentRestrictionChange) {
      onDocumentRestrictionChange(useQRSend);
    }
  }, [useQRSend, onDocumentRestrictionChange]);

  const handleToggleChange = (newUseQRSend: boolean) => {
    onToggleChange(newUseQRSend);
    if (onDocumentRestrictionChange) {
      onDocumentRestrictionChange(newUseQRSend);
    }
  };

  const getSizeStyles = () => {
    switch (size) {
      case "small":
        return {
          container: {
            width: 120,
            height: 32,
            minWidth: 100,
          },
          iconButton: {
            width: "auto",
            height: "auto",
            minWidth: 60,
            padding: "2px 8px",
          },
          icon: { fontSize: 16 },
          text: { fontSize: 12, marginLeft: 1 },
        };
      case "large":
        return {
          container: {
            width: 180,
            height: 48,
            minWidth: 180,
          },
          iconButton: {
            width: "auto",
            height: "auto",
            minWidth: 80,
            padding: "6px 14px",
          },
          icon: { fontSize: 20 },
          text: { fontSize: 14 },
        };
      default: // medium
        return {
          container: {
            width: 200,
            height: 44,
            minWidth: 200,
          },
          iconButton: {
            width: "auto",
            height: "auto",
            minWidth: 90,
            padding: "5px 12px",
          },
          icon: { fontSize: 18 },
          text: { fontSize: 13, marginLeft: 2 },
        };
    }
  };

  const sizeStyles = getSizeStyles();

  return (
    <>
      {shouldShowQRToggle ? (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            backgroundColor: theme.palette.grey[200],
            borderRadius: "20px",

            position: "relative",
            ...sizeStyles.container,
            ...sx,
          }}
        >
          {/* Active indicator background */}
          <Box
            sx={{
              position: "absolute",
              top: "2px",
              left: useQRSend ? "2px" : "calc(50% + 1px)",
              width: "calc(50% - 2px)",
              height: "calc(100% - 4px)",
              backgroundColor: theme.palette.primary.main,
              borderRadius: size === "small" ? "14px" : "18px",
              transition: "left 0.3s ease-in-out",
              zIndex: 1,
            }}
          />

          {/* QR Code Button */}
          <IconButton
            onClick={() => handleToggleChange(true)}
            disabled={!isQRAvailable}
            sx={{
              position: "relative",
              zIndex: 2,
              color: useQRSend
                ? theme.palette.primary.contrastText
                : theme.palette.text.secondary,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flex: 1,
              ...sizeStyles.iconButton,
              "&:hover": {
                backgroundColor: "transparent",
              },
              "&:disabled": {
                color: theme.palette.action.disabled,
              },
            }}
          >
            <QrCodeIcon sx={{ ...sizeStyles.icon }} />
            {leftText && (
              <Typography
                sx={{
                  ...sizeStyles.text,
                  whiteSpace: "nowrap",
                  color: useQRSend
                    ? theme.palette.primary.contrastText
                    : theme.palette.text.secondary,
                  fontWeight: 500,
                  display: "inline-block",
                  lineHeight: 1,
                }}
              >
                {leftText}
              </Typography>
            )}
          </IconButton>

          {/* Meta/WhatsApp Button */}
          <IconButton
            onClick={() => handleToggleChange(false)}
            disabled={!isMetaAvailable}
            sx={{
              position: "relative",
              zIndex: 2,
              color: !useQRSend
                ? theme.palette.primary.contrastText
                : theme.palette.text.secondary,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flex: 1,
              ...sizeStyles.iconButton,
              "&:hover": {
                backgroundColor: "transparent",
              },
              "&:disabled": {
                color: theme.palette.action.disabled,
              },
            }}
          >
            <WhatsAppIcon sx={{ ...sizeStyles.icon }} />
            {rightText && (
              <Typography
                sx={{
                  ...sizeStyles.text,
                  whiteSpace: "nowrap",
                  color: !useQRSend
                    ? theme.palette.primary.contrastText
                    : theme.palette.text.secondary,
                  fontWeight: 500,
                  display: "inline-block",
                  lineHeight: 1,
                }}
              >
                {rightText}
              </Typography>
            )}
          </IconButton>
        </Box>
      ) : (
        <></>
      )}
    </>
  );
};
